<script setup>
import PlacementsSummaryCard from '~/components/home/PlacementsSummaryCard.vue';
import {computed, ref, watch, onMounted, onUnmounted} from "vue";
import {useRouter} from "vue-router";
import {useAuthService} from "~/composables/useAuthService.js";
import {useValidation} from "~/composables/useValidation";
import {useTimelineFeed} from "~/composables/useTimelineFeed";
import {useTimelineModels} from "~/composables/useTimelineModels";
import UserTimeline from "~/components/timeline/UserTimeline.vue";
import ClickableCell from "~/components/lists/ClickableCell.vue";
import FixtureCell from "~/components/fixtures/FixtureCell.vue";
import FixtureVsCard from "~/components/fixtures/FixtureVsCard.vue";
import AvatarImageLoader from "~/components/loaders/AvatarImageLoader.vue";
import AccessApplicationCell from "~/components/users/AccessApplicationCell.vue";
import AccessApplicationUserCell from "~/components/users/AccessApplicationUserCell.vue";
import NoResults from "~/components/loaders/NoResults.vue";
import FixtureResultRow from "~/components/fixtures/FixtureResultRow.vue";
import {usePaymentsDisplay} from "~/composables/usePaymentsDisplay";
import PayEntryFeeDialog from "~/components/payments/PayEntryFeeDialog.vue";
import {owedRows, owedAction, owedActionLabel, owedIcon} from "~/composables/useOwedItems";
import {useHttpRequest} from "~/composables/useHttpRequest";
import {endpoints} from "~/network/endpoints";

// Home is a single call: GET /api/v2/timeline/feed. <user-timeline/> triggers
// the first-page load on mount; that same call populates the section refs below
// (shared useState), so every section around the feed reads from one request.
const {user, isAdminOfClub} = useAuthService();
const {money} = usePaymentsDisplay();
const router = useRouter();

const {
  managedClubs,
  leagues,
  ladders,
  tournaments,
  americanos,
  meets,
  awaitingChallenges,
  upcomingFixtures,
  upcomingMeetDates,
  liveNow,
  resultsAwaitingCount,
  outstandingCharges,
  owed,
  accessApplicationAwaitingCount,
  accessApplicationSentCount,
  pendingPlacementAwaitingCount,
  pendingPlacementCompetitions,
  feedEmpty,
  feedLoaded,
  feedErrors,
  refresh,
  refreshLiveNow,
} = useTimelineFeed();

const {fixtureHasResult} = useValidation();

// Everything the member owes (R2 batch B): each row says why and has its own
// action. Pay opens the entry fee dialog right here; the feed reloads after it closes.
const owedList = computed(() => owedRows(owed.value, outstandingCharges.value));
const {httpRequest: owedRequest} = useHttpRequest();
const payOpen = ref(false);
const payObligation = ref(null);
const payLoading = ref(null);
const onOwedAction = async (item) => {
  const action = owedAction(item);
  if (action.type === 'link') {
    await router.push(action.to);
    return;
  }
  payLoading.value = item.id;
  try {
    const mine = await owedRequest(endpoints.payments.myEntryFeeObligations) || [];
    payObligation.value = mine.find((o) => o.id === action.obligationId) || null;
    if (payObligation.value) payOpen.value = true;
    else await router.push({path: '/your-profile', query: {tab: 'payments'}});
  } catch (e) {
    await router.push({path: '/your-profile', query: {tab: 'payments'}});
  } finally {
    payLoading.value = null;
  }
};
watch(payOpen, (open) => {
  if (!open && payObligation.value) {
    payObligation.value = null;
    refresh();
  }
});

const formatMeetDateWhen = (startsAt) => {
  const date = new Date(startsAt);
  return date.toLocaleDateString('en-GB', {weekday: 'short', day: 'numeric', month: 'short'})
      + ' · ' + date.toLocaleTimeString('en-GB', {hour: '2-digit', minute: '2-digit'});
};

// "Up next" is for fixtures still to be played. Defensively drop any that already carry a
// result — some rows arrive with an approved result yet a future scheduledAt (data quirk),
// which would otherwise show an "Edit result" card under upcoming.
// A live fixture already headlines the "Live now" strip — keep it out of Up next.
const liveFixtureIds = computed(() => new Set(liveNow.value.map(l => l.fixture?.id)));
const upcomingUnplayed = computed(() =>
    upcomingFixtures.value.filter(f => !fixtureHasResult(f) && !liveFixtureIds.value.has(f.id)));

// "Live now": SignalR nudges on session start/stop; while matches are live the page
// also joins their fixture groups so every scored point refreshes the strip (debounced),
// with a slow poll as the belt-and-braces fallback.
const {on: onSignal, off: offSignal, send: sendSignal} = useSignalR();
const onLiveNowChanged = () => refreshLiveNow();
let liveNowTimer = null;
let scoreRefreshTimer = null;
const joinedFixtureIds = new Set();

const onLiveScoreUpdated = (payload) => {
  if (!payload?.fixtureId || !liveFixtureIds.value.has(payload.fixtureId)) return;
  clearTimeout(scoreRefreshTimer);
  scoreRefreshTimer = setTimeout(() => refreshLiveNow(), 1200);
};

// keep hub group membership in step with the strip's fixtures
watch(liveFixtureIds, (ids) => {
  for (const id of ids) {
    if (!joinedFixtureIds.has(id)) {
      joinedFixtureIds.add(id);
      sendSignal('JoinFixture', id).catch(() => {});
    }
  }
  for (const id of [...joinedFixtureIds]) {
    if (!ids.has(id)) {
      joinedFixtureIds.delete(id);
      sendSignal('LeaveFixture', id).catch(() => {});
    }
  }
}, {immediate: true});

onMounted(() => {
  onSignal('livenowchanged', onLiveNowChanged);
  onSignal('livescoreupdated', onLiveScoreUpdated);
  liveNowTimer = setInterval(() => {
    if (liveNow.value.length > 0) refreshLiveNow();
  }, 30000);
});
onUnmounted(() => {
  offSignal('livenowchanged', onLiveNowChanged);
  offSignal('livescoreupdated', onLiveScoreUpdated);
  if (liveNowTimer) clearInterval(liveNowTimer);
  if (scoreRefreshTimer) clearTimeout(scoreRefreshTimer);
  for (const id of joinedFixtureIds) sendSignal('LeaveFixture', id).catch(() => {});
  joinedFixtureIds.clear();
});



// Trigger the feed load here too (deduped with <user-timeline>). feedLoaded is
// shared state set only when the real fetch settles, so it stays accurate even
// when this call hits the in-flight dedup guard and returns early.
onMounted(() => refresh());

const today = computed(() =>
    new Date().toLocaleDateString('en-GB', {weekday: 'long', day: 'numeric', month: 'long'}),
);

const hasAttention = computed(() =>
    awaitingChallenges.value.length > 0 ||
    resultsAwaitingCount.value > 0 ||
    accessApplicationAwaitingCount.value > 0 ||
    pendingPlacementAwaitingCount.value > 0,
);
const hasCompetitions = computed(() =>
    leagues.value.length > 0 || ladders.value.length > 0 || tournaments.value.length > 0 ||
    americanos.value.length > 0 || meets.value.length > 0,
);

// "Your clubs" — the feed sends every club the user belongs to; clubs the user
// administers sort first so the Manage action stays prominent.
const yourClubs = computed(() =>
    [...managedClubs.value].sort((a, b) => isAdminOfClub(b.id) - isAdminOfClub(a.id)),
);

// ---- slim-card helpers (shared util mirrors Flutter TimelineTeam/PlayerRef) ----
const {teamName, teamAvatar} = useTimelineModels();
const formatExpiry = (iso) =>
    iso ? `expires ${new Date(iso).toLocaleDateString('en-GB', {weekday: 'short', day: 'numeric', month: 'long'})}` : null;

const challengeSubtitle = (c) =>
    [c.ladderName, formatExpiry(c.expiresAt)].filter(Boolean).join(' · ');

const leagueSub = (l) => [l.activeCycleName, sportDisplayName(l.sportName)].filter(Boolean).join(' · ');
const americanoSub = (a) => [a.activeCycleName, sportDisplayName(a.sportName)].filter(Boolean).join(' · ');
const meetSub = (m) => [
  m.nextOccurrenceAt
      ? `Next ${new Date(m.nextOccurrenceAt).toLocaleDateString('en-GB', {weekday: 'short', day: 'numeric', month: 'short'})}`
      : null,
  m.memberCount != null ? `${m.memberCount} member${m.memberCount === 1 ? '' : 's'}` : null,
  sportDisplayName(m.sportName),
].filter(Boolean).join(' · ');
const ladderSub = (l) => [l.teamCount != null ? `${l.teamCount} competitors` : null, sportDisplayName(l.sportName)].filter(Boolean).join(' · ');
// TournamentFormat enum → display name (API: 1 = Knockout, 2 = Groups + Knockout).
const tournamentFormatName = (fmt) => ({1: 'Knockout', 2: 'Groups + Knockout'}[fmt] || null);
const tournamentSub = (t) => [tournamentFormatName(t.format), t.teamCount ? `${t.teamCount} competitors` : null].filter(Boolean).join(' · ');

const goToChallenge = (c) => router.push(`/ladders/${c.ladderId}/challenges/${c.id}`);

// ---- data-loading dialogs (counts come from the feed; lists load on open) ----
const approvalsDialog = ref(false);
const applicationsDialog = ref(false);
const myApplicationsDialog = ref(false);
const adminClubIds = computed(() => user.value?.adminFor ?? []);

// Re-fetch the feed when a dialog closes so the attention counts update.
const onDialogClosed = () => refresh();
</script>

<template>
  <v-container>
    <!-- Mobile-only: nudge accepted members onto the app (better experience than web). -->
    <home-app-cta/>
    <!-- Greeting (app home app-bar: date + "Hi <firstName> 👋") -->
    <div class="tl-greeting mb-4">
      <div class="tl-greeting-date">{{ today }}</div>
      <h1 class="tl-greeting-name">Hi {{ user.firstName }} 👋</h1>
    </div>

    <!-- Everything you owe, the loudest thing on the page whenever it applies.
         Each row says why and has its own action; only a failed card pauses
         paid RSVPs (the reason says so). -->
    <div v-if="owedList.length" class="tl-card ht-owing mb-4">
      <div v-for="item in owedList" :key="item.kind + item.id" class="ht-owing-row">
        <v-icon size="20" color="primary">{{ owedIcon(item) }}</v-icon>
        <div class="ht-owing-body">
          <div class="t">
            <template v-if="item.amountMinor != null">{{ money(item.amountMinor, item.currency) }} · </template>{{ item.clubName }}
          </div>
          <div class="s">{{ item.reason }}</div>
        </div>
        <v-btn color="primary" size="small" :loading="payLoading === item.id" @click="onOwedAction(item)">
          {{ owedActionLabel(item) }}
        </v-btn>
      </div>
    </div>
    <pay-entry-fee-dialog v-model="payOpen" :obligation="payObligation"/>

    <!-- First load — skeleton in the shape of the dashboard (no empty-state flash) -->
    <v-row v-if="!feedLoaded" aria-hidden="true">
      <v-col order="2" order-md="1" cols="12" md="8">
        <div v-for="n in 3" :key="`m-${n}`" class="tl-card tl-skel-card mb-3">
          <div class="tl-skel-top">
            <v-skeleton-loader type="avatar"/>
            <div class="tl-skel-meta">
              <v-skeleton-loader type="text" width="45%"/>
              <v-skeleton-loader type="text" width="70%"/>
            </div>
          </div>
          <v-skeleton-loader type="image" height="110" class="tl-skel-media"/>
        </div>
      </v-col>
      <v-col order="1" order-md="2" cols="12" md="4">
        <div class="tl-side-sticky">
          <div v-for="n in 3" :key="`s-${n}`" class="tl-card tl-skel-list mb-3">
            <v-skeleton-loader type="list-item-avatar-two-line"/>
          </div>
        </div>
      </v-col>
    </v-row>

    <v-row v-else>
      <!-- ════════ MAIN COLUMN ════════ -->
      <v-col order="2" order-md="1" cols="12" md="8">

        <!-- Live now — club matches being scored as you read this. Reuses the standard
             result row (score pre-shaped by the API) with the LIVE chip in live mode. -->
        <template v-if="liveNow.length > 0">
          <div class="tl-section-head mt-0"><h2>Live now</h2></div>
          <fixture-result-row
              v-for="live in liveNow"
              :key="'live-' + live.fixture.id"
              :fixture="live.fixture"
              :live="true"
          />
        </template>

        <!-- Needs your attention -->
        <template v-if="hasAttention">
          <div class="tl-section-head mt-0"><h2>Needs your attention</h2></div>

          <!-- awaitingChallenges[] — Respond goes to the ladder challenge page -->
          <div v-for="c in awaitingChallenges" :key="c.id" class="tl-card tl-attention mb-3">
            <avatar-image-loader
                v-if="teamAvatar(c.challengingTeam)"
                :image-file-name="teamAvatar(c.challengingTeam)"
                :image-clickable="false"
                size="42"
            />
            <v-avatar v-else size="42" color="brandTint">
              <v-icon color="primary">mdi-sword-cross</v-icon>
            </v-avatar>
            <div class="tl-attention-who">
              <div class="tl-attention-title">{{ teamName(c.challengingTeam) }} challenged you</div>
              <div class="tl-attention-sub">{{ challengeSubtitle(c) }}</div>
            </div>
            <v-btn color="primary" variant="flat" size="small" rounded="pill"
                   prepend-icon="mdi-reply" @click="goToChallenge(c)">Respond</v-btn>
          </div>

          <!-- resultsAwaitingCount → dialog of fixtures awaiting your approval -->
          <div v-if="resultsAwaitingCount > 0" class="tl-card tl-attention mb-3">
            <v-avatar size="42" color="warningTint">
              <v-icon color="warning">mdi-clipboard-check-outline</v-icon>
            </v-avatar>
            <div class="tl-attention-who">
              <div class="tl-attention-title">
                {{ resultsAwaitingCount === 1 ? 'Result awaiting your approval' : `${resultsAwaitingCount} results awaiting your approval` }}
              </div>
              <div class="tl-attention-sub">Tap to review and approve</div>
            </div>
            <v-btn variant="outlined" size="small" rounded="pill" @click="approvalsDialog = true">Review</v-btn>
          </div>

          <!-- accessApplicationAwaitingCount (admins) → dialog of join requests -->
          <div v-if="accessApplicationAwaitingCount > 0" class="tl-card tl-attention mb-3">
            <v-avatar size="42" color="brandTint">
              <v-icon color="primary">mdi-account-plus-outline</v-icon>
            </v-avatar>
            <div class="tl-attention-who">
              <div class="tl-attention-title">
                {{ accessApplicationAwaitingCount === 1 ? 'Application awaiting your approval' : `${accessApplicationAwaitingCount} applications awaiting your approval` }}
              </div>
              <div class="tl-attention-sub">Tap to approve or reject join requests</div>
            </div>
            <v-btn variant="outlined" size="small" rounded="pill" @click="applicationsDialog = true">Review</v-btn>
          </div>

          <!-- pendingPlacementAwaitingCount (admins) → the competitions' waiting areas -->
          <placements-summary-card :count="pendingPlacementAwaitingCount" :competitions="pendingPlacementCompetitions"/>
        </template>

        <!-- My applications — accessApplicationSentCount → dialog (review/cancel) -->
        <template v-if="accessApplicationSentCount > 0">
          <div class="tl-section-head"><h2>My applications</h2></div>
          <button type="button" class="tl-card comp-row tl-comp-button mb-3" @click="myApplicationsDialog = true">
            <div class="comp-icon"><v-icon color="primary" size="22" icon="mdi-tray-arrow-up"/></div>
            <div class="comp-who">
              <div class="comp-title">
                {{ accessApplicationSentCount === 1 ? '1 pending request' : `${accessApplicationSentCount} pending requests` }}
              </div>
              <div class="comp-sub">Awaiting approval. Tap to review or cancel</div>
            </div>
            <v-icon icon="mdi-chevron-right" size="20" class="comp-chev"/>
          </button>
        </template>

        <!-- Up next — the next meet night you're confirmed for, then
             upcomingFixtures[] (reusable FixtureVsCard, slim-feed) -->
        <template v-if="upcomingUnplayed.length > 0 || upcomingMeetDates.length > 0">
          <div class="tl-section-head"><h2>Up next</h2></div>
          <button
              v-for="date in upcomingMeetDates.slice(0, 1)"
              :key="`md-${date.id}`"
              class="tl-card upnext-meet"
              @click="navigateTo(`/meets/${date.meetId}/events/${date.id}`)"
          >
            <span class="upnext-meet-icon">
              <v-icon icon="mdi-handshake-outline" size="20"/>
            </span>
            <span class="upnext-meet-body">
              <span class="upnext-meet-name">{{ date.meetName }}</span>
              <span class="upnext-meet-sub">
                {{ formatMeetDateWhen(date.startsAt) }} · you're in{{
                  (date.attendingCount ?? 0) > 1 ? ` with ${date.attendingCount - 1} ${date.attendingCount === 2 ? 'other' : 'others'}` : ''
                }}
              </span>
            </span>
            <v-icon icon="mdi-chevron-right" size="20" class="comp-chev"/>
          </button>
          <fixture-vs-card
              v-for="fixture in upcomingUnplayed"
              :key="fixture.id"
              :fixture="fixture"
          />
        </template>

        <!-- Activity feed (items[] + pinned[], cursor-paged) -->
        <div class="tl-section-head"><h2>Activity</h2></div>
        <user-timeline/>
      </v-col>

      <!-- ════════ SIDE RAIL (mobile: ordered first) ════════ -->
      <v-col order="1" order-md="2" cols="12" md="4">
        <div class="tl-side-sticky">

          <!-- Your clubs — managedClubs[] (every club the user belongs to; the
               property name is legacy). The card links to the club page; clubs
               the user administers also get a ghost "Manage" button. -->
          <template v-if="yourClubs.length > 0">
            <div class="tl-section-head mt-0">
              <h2>{{ yourClubs.length === 1 ? 'Your club' : 'Your clubs' }}</h2>
            </div>
            <div v-for="club in yourClubs" :key="club.id" class="tl-card tl-manage mb-3"
                 @click="router.push({ name: 'ClubDetail', params: { clubId: club.id } })">
              <avatar-image-loader
                  v-if="club.imageFileName"
                  :image-file-name="club.imageFileName"
                  :image-clickable="false"
                  :rounded="false"
                  size="44"
                  class="tl-manage-crest"
              />
              <div v-else class="tl-manage-crest tl-manage-crest--fallback">
                <v-icon icon="mdi-account-group" size="24"/>
              </div>
              <div class="tl-manage-who">
                <div class="tl-manage-name">{{ club.name }}</div>
                <div class="tl-manage-members">{{ club.memberCount === 1 ? '1 member' : `${club.memberCount ?? 0} members` }}</div>
              </div>
              <v-btn v-if="isAdminOfClub(club.id)" variant="outlined" size="small" rounded="pill"
                     prepend-icon="mdi-cog-outline" @click.stop
                     :to="{ name: 'ClubDashboard', params: { clubId: club.id } }">Manage</v-btn>
            </div>
          </template>

          <!-- Your competitions — leagues[] / americanos[] / ladders[] / tournaments[] / meets[] -->
          <template v-if="hasCompetitions">
            <div class="tl-section-head" :class="{ 'mt-0': managedClubs.length === 0 }">
              <h2>Your competitions</h2>
            </div>
            <clickable-cell
                v-for="league in leagues" :key="`l-${league.id}`"
                tone="league" icon="mdi-table" :title="league.name" :subtitle="leagueSub(league)"
                :to="{ name: 'LeagueDetail', params: { leagueId: league.id } }"
            >
              <template v-if="league.awaitingPlacement" #trailing>
                <span class="tl-chip tl-chip--warn comp-chip"><v-icon size="12" icon="mdi-account-clock-outline" class="mr-1"/>Waiting to be placed</span>
              </template>
            </clickable-cell>
            <clickable-cell
                v-for="americano in americanos" :key="`a-${americano.id}`"
                tone="americano" icon="mdi-account-switch" :title="americano.name" :subtitle="americanoSub(americano)"
                :to="{ name: 'AmericanoDetail', params: { americanoId: americano.id } }"
            >
              <template v-if="americano.awaitingPlacement" #trailing>
                <span class="tl-chip tl-chip--warn comp-chip"><v-icon size="12" icon="mdi-account-clock-outline" class="mr-1"/>Waiting to be placed</span>
              </template>
            </clickable-cell>
            <clickable-cell
                v-for="ladder in ladders" :key="`d-${ladder.id}`"
                tone="ladder" icon="mdi-ladder" :title="ladder.name" :subtitle="ladderSub(ladder)"
                :to="{ name: 'LadderDetail', params: { ladderId: ladder.id } }"
            >
              <template v-if="ladder.awaitingPlacement" #trailing>
                <span class="tl-chip tl-chip--warn comp-chip"><v-icon size="12" icon="mdi-account-clock-outline" class="mr-1"/>Waiting to be placed</span>
              </template>
            </clickable-cell>
            <clickable-cell
                v-for="tournament in tournaments" :key="`t-${tournament.id}`"
                tone="tournament" icon="mdi-trophy" :title="tournament.name" :subtitle="tournamentSub(tournament)"
                :to="{ name: 'TournamentDetail', params: { tournamentId: tournament.id } }"
            />
            <clickable-cell
                v-for="meet in meets" :key="`m-${meet.id}`"
                tone="meet" icon="mdi-handshake" :title="meet.name" :subtitle="meetSub(meet)"
                :to="{ name: 'MeetDetail', params: { meetId: meet.id } }"
            />
          </template>
        </div>
      </v-col>
    </v-row>

    <!-- ════════ DIALOGS (data loaded on open via CrudListLoader) ════════ -->

    <!-- Results awaiting your approval -->
    <v-dialog v-model="approvalsDialog" max-width="600" @after-leave="onDialogClosed">
      <div class="tl-card pa-4">
        <div class="d-flex align-center mb-3">
          <h3 class="flex-grow-1">Results awaiting your approval</h3>
          <v-btn icon="mdi-close" variant="text" size="small" @click="approvalsDialog = false"/>
        </div>
        <crud-list-loader
            v-if="approvalsDialog"
            path="api/v2/fixtures"
            :listeners="['fixtureUpdated']"
            :per-page="50"
            :custom-filters="{ approvalRequiredUserId: user.id }">
          <template #empty>
            <no-results>Nothing awaiting your approval.</no-results>
          </template>
          <template #default="{ items: fixtures }">
            <fixture-result-row
                v-for="fixture in fixtures"
                :key="fixture.id"
                class="mb-3"
                :fixture="fixture"
                @action="approvalsDialog = false"
            />
          </template>
        </crud-list-loader>
      </div>
    </v-dialog>

    <!-- Applications awaiting your approval (admin) -->
    <v-dialog v-model="applicationsDialog" max-width="600" @after-leave="onDialogClosed">
      <div class="tl-card pa-4">
        <div class="d-flex align-center mb-3">
          <h3 class="flex-grow-1">Applications awaiting your approval</h3>
          <v-btn icon="mdi-close" variant="text" size="small" @click="applicationsDialog = false"/>
        </div>
        <crud-list-loader
            v-if="applicationsDialog"
            path="api/v2/accessApplications"
            :listeners="['requestsUpdated', 'accessApplicationUpdated']"
            :per-page="50"
            :custom-filters="{ clubIds: adminClubIds }">
          <!-- We already know how many are pending from the feed count, so match the
               skeleton count to avoid a layout jump on load. -->
          <template #loader="{ empty }">
            <template v-if="empty">
              <v-skeleton-loader
                  v-for="n in Math.min(Math.max(accessApplicationAwaitingCount, 1), 50)"
                  :key="n"
                  type="list-item-avatar-two-line"
                  class="mb-2"
              />
            </template>
          </template>
          <template #empty>
            <no-results>No join requests to review.</no-results>
          </template>
          <template #default="{ items: requests }">
            <access-application-cell
                v-for="request in requests"
                :key="request.id"
                :request="request"
            />
          </template>
        </crud-list-loader>
      </div>
    </v-dialog>

    <!-- My applications (review / cancel) -->
    <v-dialog v-model="myApplicationsDialog" max-width="600" @after-leave="onDialogClosed">
      <div class="tl-card pa-4">
        <div class="d-flex align-center mb-3">
          <h3 class="flex-grow-1">My applications</h3>
          <v-btn icon="mdi-close" variant="text" size="small" @click="myApplicationsDialog = false"/>
        </div>
        <crud-list-loader
            v-if="myApplicationsDialog"
            path="api/v2/accessApplications"
            :listeners="['requestsUpdated', 'accessApplicationUpdated']"
            :per-page="50"
            :custom-filters="{ userIds: [user.id] }">
          <template #empty>
            <no-results>You have no pending applications.</no-results>
          </template>
          <template #default="{ items: myApplications }">
            <v-list bg-color="transparent">
              <access-application-user-cell
                  v-for="application in myApplications"
                  :key="application.id"
                  :application="application"
              />
            </v-list>
          </template>
        </crud-list-loader>
      </div>
    </v-dialog>
  </v-container>
</template>

<style scoped lang="scss">

/* first-load skeletons — shaped like the feed cards / side-rail rows */
.tl-skel-card {
  padding: 16px;
}

.tl-skel-top {
  display: flex;
  align-items: center;
  gap: 12px;
}

.tl-skel-meta {
  flex: 1 1 auto;
  min-width: 0;
}

.tl-skel-media {
  margin-top: 14px;
  border-radius: 10px;
  overflow: hidden;
}

.tl-skel-list {
  padding: 4px 8px;
}

.tl-skel-card :deep(.v-skeleton-loader),
.tl-skel-list :deep(.v-skeleton-loader) {
  background: transparent;
}

.tl-skel-top :deep(.v-skeleton-loader__avatar) {
  margin: 0;
}

.tl-skel-meta :deep(.v-skeleton-loader__text) {
  margin-bottom: 8px;

  &:last-child {
    margin-bottom: 0;
  }
}

.tl-greeting-date {
  font-size: 12px;
  font-weight: 500;
  color: rgb(var(--v-theme-textSubtle));
}

.tl-greeting-name {
  font-size: 26px;
  font-weight: 800;
  letter-spacing: -0.6px;
  line-height: 1.2;
}





/* your-clubs card — square crest + who + ghost Manage button (admins only) */
.tl-manage {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  cursor: pointer;

  .tl-manage-crest {
    width: 44px;
    height: 44px;
    border-radius: 12px !important;
    flex-shrink: 0;
  }

  .tl-manage-crest--fallback {
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgb(var(--v-theme-brandTint));
    color: rgb(var(--v-theme-primary));
  }

  .tl-manage-who {
    flex: 1 1 auto;
    min-width: 0;
  }

  .tl-manage-name {
    font-size: 14px;
    font-weight: 700;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .tl-manage-members {
    font-size: 11.5px;
    color: rgb(var(--v-theme-textSubtle));
    margin-top: 2px;
  }
}

/* attention card — avatar + who + action (mockups/redesign .card.challenge) */
.tl-attention {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;

  .tl-attention-who {
    flex: 1 1 auto;
    min-width: 0;
  }

  .tl-attention-title {
    font-size: 13.5px;
    font-weight: 600;
    line-height: 1.3;
  }

  .tl-attention-sub {
    font-size: 11.5px;
    color: rgb(var(--v-theme-textSubtle));
    margin-top: 1px;
  }
}

/* a comp-row rendered as a button (opens a dialog instead of navigating) */
.tl-comp-button {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 13px 14px;
  text-align: left;
  background: rgb(var(--v-theme-surface));
  cursor: pointer;

  .comp-icon {
    width: 40px;
    height: 40px;
    border-radius: 11px;
    background: rgb(var(--v-theme-brandTint));
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .comp-who {
    flex: 1 1 auto;
    min-width: 0;
  }

  .comp-title {
    font-size: 15px;
    font-weight: 700;
    letter-spacing: -0.2px;
  }

  .comp-sub {
    font-size: 12.5px;
    color: rgb(var(--v-theme-textSubtle));
    margin-top: 2px;
  }

  .comp-chev {
    color: rgb(var(--v-theme-textSubtle));
  }
}

/* sticky side rail on desktop — scrolls internally when taller than the viewport */
.tl-side-sticky {
  @media (min-width: 960px) {
    position: sticky;
    top: 80px;
    max-height: calc(100vh - 96px);
    overflow-y: auto;
    overflow-x: hidden;
  }
}

.ht-owing {
  border: 1px solid rgb(var(--v-theme-primary));
  padding: 4px 0;
}

.ht-owing-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;

  & + .ht-owing-row {
    border-top: 1px solid rgb(var(--v-theme-borderLight));
  }
}

.ht-owing-body {
  flex: 1;
  min-width: 0;

  .t {
    font-size: 13px;
    font-weight: 700;
  }

  .s {
    font-size: 11.5px;
    color: rgb(var(--v-theme-textSubtle));
  }
}

/* Up next — the next confirmed meet night */
.upnext-meet {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  text-align: left;
  padding: 14px 16px;
  margin-bottom: 10px;
  cursor: pointer;
}

.upnext-meet-icon {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: rgb(var(--v-theme-successTint));
  color: rgb(var(--v-theme-success));
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.upnext-meet-body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.upnext-meet-name {
  font-size: 14px;
  font-weight: 700;
  color: rgb(var(--v-theme-text));
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.upnext-meet-sub {
  font-size: 12px;
  color: rgb(var(--v-theme-textSubtle));
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
